import React, { useState } from "react"
import Navbar from "../../components/Navbar/Navbar"
import { useNavigate } from "react-router-dom"
import {
  Table,
  Select,
  Button,
  Modal,
  Row,
  Col,
  Form,
  DatePicker,
  Checkbox,
  Tabs,
  Input,
} from "antd"
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import dayjs from "dayjs"
import moment from "moment"
import { UsePostRequest } from "../../utils/api/ApiRequests"
import { GetAllEmployees } from "../../utils/api/DropdownFiltersApi"
import { usePostRequest } from "../../hooks/usePostRequest"
import { getIsAdminLogin, getIsApprover, getEmployeeId } from "../../utils/Common"
import * as XLSX from "xlsx"

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false)
  const values = Form.useWatch([], form)
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true)
        },
        () => {
          setSubmittable(false)
        }
      )
  }, [values])

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  )
}

// const {Dragger} = Upload;

export default function Leave() {
  const [form] = Form.useForm()
  const isAdminLogin = getIsAdminLogin()
  const isApprover = getIsApprover()
  const employeeId = getEmployeeId()
  const navigate = useNavigate()
  const dateToday = new Date()
  const [content, setContent] = useState("")
  const [tableData, setTableData] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [clickedRowData, setClickedRowData] = useState({})
  const [leaveTypes, setLetterTypes] = useState([])
  const [fileList, setFileList] = useState([])
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false)
  const [hasLeaveCredit, setHasLeaveCredit] = useState(true)
  const [modal, contextHolder] = Modal.useModal()
  const employeeOptions = GetAllEmployees()
  const dateFormat = "YYYY-MM-DD"
  const [searchTerm, setSearchTerm] = useState(null)

  const { callPost, isLoading } = usePostRequest(() => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowViewModal(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  })

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    employee_id: isApprover ? null : employeeId,
    status: "pending",
    year: dateToday.getFullYear(),
  })

  const tableColumns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
    },
    {
      title: "Date Applied",
      dataIndex: "added_on",
      key: "added_on",
      render: (added_on, record) => (
        <div>{moment(added_on).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Date From",
      dataIndex: "date_from",
      key: "date_from",
      render: (date_from, record) => (
        <div>{moment(date_from).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Date To",
      dataIndex: "date_to",
      key: "date_to",
      render: (date_to, record) => (
        <div>{moment(date_to).format("MMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Days",
      dataIndex: "no_of_days",
      key: "no_of_days",
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      //   render: (details, record) => (
      //     <ul
      //       onClick={() => handleRowClick(record)}
      //       className="pointer-cursor custom-list"
      //     >
      //       {details.map((value, index) => (
      //         <li key={index}>{value.leave_to}</li>
      //       ))}
      //     </ul>
      //   ),
    },
  ]

  //  Don't delete this commented function coz will use this later
  // function getBase64(file) {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = (error) => reject(error);
  //   });
  // }

  const checkLeaveCredit = async (empId, leaveTypeId) => {
    if (!empId || !leaveTypeId) return
    try {
      const response = await UsePostRequest("leave_credits/get", {
        employee_id: empId,
        leave_type_id: leaveTypeId,
      })
      const leaveTypesData = response?.data || []
      let remaining = 0
      if (Array.isArray(leaveTypesData) && leaveTypesData.length > 0) {
        const empLeaves = leaveTypesData[0]?.employee_leaves || []
        if (Array.isArray(empLeaves) && empLeaves.length > 0) {
          remaining = Number(empLeaves[0]?.remaining || 0)
        }
      }

      if (remaining <= 0) {
        setHasLeaveCredit(false)
        const confirmModal = modal?.confirm || Modal.confirm
        confirmModal({
          title: "No Available Leave Credit",
          icon: <ExclamationCircleOutlined />,
          content: "No available leave credit. Continue leave without pay?",
          okText: "Continue",
          cancelText: "Cancel",
          onOk: () => {
            form.setFieldsValue({ is_without_pay: true })
          },
          onCancel: () => {
            form.setFieldsValue({ leave_id: null, is_without_pay: false })
            setHasLeaveCredit(true)
          },
        })
      } else {
        setHasLeaveCredit(true)
        form.setFieldsValue({ is_without_pay: false })
      }
    } catch (err) {
      console.error(err)
    }
  }

  const onFinish = async (formInputs) => {
    formInputs.date_from = formInputs.date_from.format(dateFormat)
    formInputs.date_to = formInputs.date_to.format(dateFormat)
    formInputs.status = "pending"
    formInputs.is_half_day = formInputs.is_half_day ? "1" : "0"
    formInputs.is_without_pay = formInputs.is_without_pay ? "1" : "0"

    formInputs.file_names = []

    callPost("employee_leaves/add", formInputs, false)
  }

  const onEdit = async (formInputs) => {
    formInputs.employee_leave_id = clickedRowData.id
    formInputs.date_from = formInputs.date_from.format(dateFormat)
    formInputs.date_to = formInputs.date_to.format(dateFormat)
    formInputs.status = clickedRowData.status
    formInputs.is_half_day = formInputs.is_half_day ? "1" : "0"
    formInputs.is_without_pay = formInputs.is_without_pay ? "1" : "0"

    // const tempFiles = fileList.map(async (file) => {
    //     var tempFile = await getBase64(file);
    //     return tempFile;
    // });
    // const tempPromise = await Promise.all(tempFiles);
    formInputs.file_names = []
    callPost(`employee_leaves/update`, formInputs, false)
  }

  const confirmApprove = (leave) => {
    modal.confirm({
      title: `Are you sure you want to approve leave ID "${leave.id}"?`,
      icon: <CheckCircleOutlined />,
      okText: "Approve",
      cancelText: "Cancel",
      onOk: () => handleApprove(leave.id),
    })
  }

  function handleApprove(passedId) {
    callPost(
      "employee_leaves/approve",
      { employee_leave_id: passedId, status: "approved" },
      false
    )
    setShowViewModal(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  }
  
  const confirmDecline = (leave) => {
    modal.confirm({
      title: `Are you sure you want to decline leave ID "${leave.id}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Decline",
      cancelText: "Cancel",
      onOk: () => handleDecline(leave.id),
      okType: "danger",
    })
  }

  function handleDecline(passedId) {
    callPost(
      "employee_leaves/decline",
      { employee_leave_id: passedId },
      false
    )
    setShowViewModal(false)
    resetValues()
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }))
  }

  const confirmDelete = (leave) => {
    modal.confirm({
      title: `Are you sure you want to delete leave ID "${leave.id}"?`,
      icon: <ExclamationCircleOutlined />,
      content: "You cannot undo this action.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => handleDelete(leave.id),
      okType: "danger",
    })
  }

  function handleDelete(passedId) {
    callPost("employee_leaves/delete", { employee_leave_id: passedId }, false)
    resetValues()
  }

  function resetValues() {
    form.resetFields()
    setContent("")
    setFileList([])
    setHasLeaveCredit(true)
  }

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }))
    }
  }

  const handleRowClick = (rowData) => {
    if (isApprover) {
      setContent(rowData.remarks)
      setClickedRowData(rowData)
      const formattedValues = { ...rowData }
      formattedValues.date_from = formattedValues.date_from
        ? dayjs(formattedValues.date_from, dateFormat)
        : ""
      formattedValues.date_to = formattedValues.date_to
        ? dayjs(formattedValues.date_to, dateFormat)
        : ""
      formattedValues.is_half_day =
        formattedValues.is_half_day && (formattedValues.is_half_day === "1" || formattedValues.is_half_day === true || formattedValues.is_half_day === 1)
          ? true
          : false
      formattedValues.is_without_pay =
        formattedValues.is_without_pay && (formattedValues.is_without_pay === "1" || formattedValues.is_without_pay === true || formattedValues.is_without_pay === 1)
          ? true
          : false
      form.setFieldsValue(formattedValues)
      setHasLeaveCredit(!formattedValues.is_without_pay)
      setShowViewModal(true)
    } else {
      navigate("view", { state: rowData })
    }
  }

  const rowProps = (record) => ({
    onClick: () => handleRowClick(record),
  })

  const handleQuillChange = (html) => {
    setContent(html)
    form.setFieldValue("remarks", html)
  }

  function exportToExcel() {
    const excelData = [tableColumns.map((col) => col.title)] // insert header titles first
    // populate excel data
    tableData.forEach((data) => {
      let tempArray = tableColumns.map((col) => data[col.key])
      excelData.push(tempArray)
    })
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.aoa_to_sheet(excelData)
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1")
    const excelBlob = new Blob(
      [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
      { type: "application/octet-stream" }
    )
    const excelUrl = URL.createObjectURL(excelBlob)
    const downloadAnchor = document.createElement("a")
    downloadAnchor.href = excelUrl
    downloadAnchor.download = "Employee Leave.xlsx"
    downloadAnchor.click()
    setTimeout(() => {
      URL.revokeObjectURL(excelUrl)
    }, 100)
  }

  //  Don't delete this commented function coz will use this later
  // const handleRemove = (selectedFile) => {
  //   var newlist = fileList.filter((file) => {
  //     return selectedFile.uid !== file.uid;
  //   });
  //   setFileList(newlist);
  // };

  //  Don't delete this commented function coz will use this later
  // const handleBeforeUpload = (file) => {
  //   const isSizeValid = file.size / 1024 / 1024 < 5; // Check if file size is less than 2MB (backend request)

  //   if (!isSizeValid) {
  //     setFileSizeExceeded(true);
  //   } else {
  //     setFileList([...fileList, file]);
  //   }
  // };

  React.useEffect(() => {
    async function fetchLetterTypes() {
      const response = await UsePostRequest("leave_types/get", {})
      if (response.data) {
        const res = response.data.map((data) => {
          var info = {}
          info.value = data.id
          info.label = data.description
          return info
        })
        setLetterTypes(res)
      }
    }
    fetchLetterTypes()
  }, [])

  React.useEffect(() => {
    const searchTimeout = setTimeout(() => {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }))
    }, 2000)

    return () => {
      clearTimeout(searchTimeout)
    }
  }, [searchTerm])

  React.useEffect(() => {
    async function fetchTableData() {
      setIsFetching(true)
      setTableData([])
      const response = await UsePostRequest("employee_leaves/get", filterConfig)
      if (response.data) {
        setTableData(response.data)
      }
      setIsFetching(false)
    }

    fetchTableData()
  }, [filterConfig])

  return (
    <Navbar
      pageTitle="Leaves"
      isDownloadBtn
      pageButtonTitle="Download Excel"
      onClickDownloadButton={exportToExcel}
      pageButtonTitle2="Add"
      onClickButton={() => {
        resetValues()
        setShowAddModal(true)
        setShowViewModal(false)
      }}
      searchPlaceholder="employee"
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      isManager
      searchable={isApprover}
      withDateRangePicker
      onYearChange={(e) => {
        if (e) {
          setFilterConfig((prev) => ({ ...prev, year: e.$y }))
        } else {
          setFilterConfig((prev) => ({ ...prev, year: null }))
        }
      }}
    >
      <Tabs
        defaultActiveKey="pending"
        className="mt-4"
        items={[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "declined", label: "Declined" },
          { key: "", label: "All" },
        ]}
        type="card"
        onChange={(e) => setFilterConfig((prev) => ({ ...prev, status: e }))}
      />
      <Table
        className="mt-4"
        rowKey="id"
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching}
        onRow={(record) => ({
          ...rowProps(record),
          style: { cursor: "pointer" },
        })}
      />
      <Modal
        title={`${showAddModal ? "Add" : "Edit"} Leave`}
        centered
        open={showAddModal || showEditModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false)
          setShowEditModal(false)
          resetValues()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={showAddModal ? onFinish : onEdit}
          initialValues={isAdminLogin || !employeeId || Number(employeeId) === 0 ? undefined : { employee_id: Number(employeeId) }}
        >
          <Row>
            {isAdminLogin ? (
              <Col xs={24} lg={12}>
                <Form.Item
                  name="employee_id"
                  label="Employee"
                  rules={[
                    {
                      required: true,
                      message: "Please select an employee!",
                    },
                  ]}
                >
                  <Select
                    options={employeeOptions.slice(1)}
                    showSearch
                    placeholder="Select an employee"
                    allowClear
                    onChange={(val) => {
                      const currentLeaveId = form.getFieldValue("leave_id")
                      if (currentLeaveId) {
                        checkLeaveCredit(val, currentLeaveId)
                      }
                    }}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            ) : (
              <Col xs={24} lg={12}>
                <Form.Item name="employee_id" label="Employee">
                  <Select
                    options={employeeOptions.slice(1)}
                    showSearch
                    disabled
                    defaultValue={employeeId && Number(employeeId) > 0 ? Number(employeeId) : undefined}
                    placeholder="Select an employee"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} lg={12}>
              <Form.Item
                name="leave_id"
                label="Leave Type"
                rules={[
                  {
                    required: true,
                    message: "Please select a leave type!",
                  },
                ]}
              >
                <Select
                  options={leaveTypes}
                  placeholder="Select a leave type"
                  onChange={(val) => {
                    const currentEmpId = form.getFieldValue("employee_id") || employeeId
                    checkLeaveCredit(currentEmpId, val)
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="date_from"
                label="Date From"
                rules={[
                  {
                    required: true,
                    message: "Please select date from!",
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="date_to"
                label="Date To"
                rules={[
                  {
                    required: true,
                    message: "Please select date to!",
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
              <Form.Item name="is_half_day" valuePropName="checked">
                <Checkbox className="me-2" /> Half Day
              </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
              <Form.Item name="is_without_pay" valuePropName="checked">
                <Checkbox className="me-2" disabled={hasLeaveCredit} /> Without Pay
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="remarks"
                label="Reason"
                rules={[
                  {
                    required: true,
                    message: "Please enter reason for leave.",
                  },
                ]}
              >
                <Input.TextArea rows={3} placeholder="Enter reason for leave" />
              </Form.Item>
            </Col>
          </Row>

          {/* <Row className="mb-3">
            Attachments
            <Col span={24}>
              <Dragger
                {...{
                  fileList: fileList,
                  onRemove: handleRemove,
                  beforeUpload: handleBeforeUpload,
                  multiple: false,
                  listType: "picture",
                  progress: { showInfo: true },
                  data: (file) => {},
                }}
              >
                <CloudUploadOutlined className="upload-icon" />
                <p className="ant-upload-text">Browse Files</p>
              </Dragger>
            </Col>
          </Row> */}

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowAddModal(false)
                    setShowEditModal(false)
                    resetValues()
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Review Leave"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false)
          resetValues()
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="editForm"
          layout="vertical"
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  options={employeeOptions.slice(1)}
                  searchable
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="leave_id"
                label="Leave Type"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select options={leaveTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="date_from"
                label="Date From"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker disabled style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="date_to"
                label="Date To"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker disabled style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
              <Form.Item name="is_half_day" valuePropName="checked">
                <Checkbox className="me-2" disabled /> Half Day
              </Form.Item>
            </Col>
            <Col xs={24} lg={6}>
              <Form.Item name="is_without_pay" valuePropName="checked">
                <Checkbox className="me-2" disabled /> Without Pay
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item name="remarks" label="Reason">
                <Input.TextArea rows={3} disabled />
              </Form.Item>
            </Col>
          </Row>

          {/* <Row className="mb-3">
            Attachments
            <Col span={24}>
              <Dragger
                {...{
                  fileList: fileList,
                  onRemove: handleRemove,
                  beforeUpload: handleBeforeUpload,
                  multiple: false,
                  listType: "picture",
                  progress: { showInfo: true },
                  data: (file) => {},
                }}
                disabled
              >
                <CloudUploadOutlined className="upload-icon" />
                <p className="ant-upload-text">Browse Files</p>
              </Dragger>
            </Col>
          </Row> */}

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Button
                danger
                onClick={() => {
                  setShowViewModal(false)
                  resetValues()
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="text"
                onClick={() => {
                  setShowViewModal(false)
                  setShowEditModal(true)
                }}
                icon={<EditOutlined />}
                disabled={clickedRowData.status === "approved" || clickedRowData.status === "declined" || !isApprover || clickedRowData.employee_id == employeeId}
              >
                Edit
              </Button>
              <Button
                type="text"
                onClick={() => {
                  confirmApprove(clickedRowData)
                }}
                icon={<CheckCircleOutlined />}
                disabled={clickedRowData.status === "approved" || clickedRowData.status === "declined" || !isApprover || clickedRowData.employee_id == employeeId}
              >
                Approve
              </Button>
              <Button
                type="text"
                danger
                onClick={() => {
                  confirmDecline(clickedRowData)
                }}
                icon={<ExclamationCircleOutlined />}
                disabled={clickedRowData.status === "approved" || clickedRowData.status === "declined" || !isApprover || clickedRowData.employee_id == employeeId}
              >
                Decline
              </Button>
              <Button
                type="text"
                onClick={() => {
                  navigate("view", { state: clickedRowData })
                }}
                icon={<EyeOutlined />}
              >
                View
              </Button>
              <Button
                type="text"
                danger
                onClick={() => {
                  confirmDelete(clickedRowData)
                }}
                icon={<DeleteOutlined />}
                disabled={clickedRowData.status === "approved" || clickedRowData.status === "declined"}
              >
                Delete
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={fileSizeExceeded}
        title={""}
        footer={null}
        onCancel={() => setFileSizeExceeded(false)}
      >
        File size must not exceed 5MB.
      </Modal>
      {contextHolder}
    </Navbar>
  )
}